import { useState, useRef } from "react";
import { Upload, X, Image as ImageIcon, Loader2 } from "lucide-react";
import api from "@/lib/api";
import { toast } from "sonner";

/**
 * FileUploader - Upload images or documents to Emergent Object Storage.
 * Returns array of { id, download_url, original_filename } via onChange.
 */
export default function FileUploader({
  value = [],
  onChange,
  category = "general",
  accept = "image/jpeg,image/png,image/webp,image/jpg,application/pdf",
  maxFiles = 5,
  maxSizeMB = 25,
  label = "Dosya Yükle",
  testid,
}) {
  const [uploading, setUploading] = useState(false);
  const inputRef = useRef(null);

  const upload = async (files) => {
    if (!files?.length) return;
    if (value.length + files.length > maxFiles) {
      toast.error(`En fazla ${maxFiles} dosya yüklenebilir`);
      return;
    }
    setUploading(true);
    const next = [...value];
    try {
      for (const f of files) {
        if (f.size > maxSizeMB * 1024 * 1024) {
          toast.error(`${f.name}: Maksimum ${maxSizeMB}MB`);
          continue;
        }
        const fd = new FormData();
        fd.append("file", f);
        fd.append("category", category);
        const { data } = await api.post("/files/upload", fd, {
          headers: { "Content-Type": "multipart/form-data" },
        });
        next.push({
          id: data.id,
          download_url: data.download_url,
          original_filename: data.original_filename,
          content_type: data.content_type,
        });
      }
      onChange(next);
      toast.success("Dosya yüklendi");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Dosya yüklenemedi");
    } finally {
      setUploading(false);
      if (inputRef.current) inputRef.current.value = "";
    }
  };

  const remove = (idx) => {
    onChange(value.filter((_, i) => i !== idx));
  };

  const isImage = (f) => f.content_type?.startsWith("image/");
  const BACKEND = process.env.REACT_APP_BACKEND_URL;
  const token = localStorage.getItem("azd_token");
  const urlFor = (f) => `${BACKEND}${f.download_url}?auth=${token}`;

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-center gap-3">
        <input
          ref={inputRef}
          type="file"
          accept={accept}
          multiple={maxFiles > 1}
          onChange={(e) => upload(Array.from(e.target.files || []))}
          className="hidden"
          data-testid={testid ? `${testid}-input` : "file-input"}
        />
        <button
          type="button"
          onClick={() => inputRef.current?.click()}
          disabled={uploading || value.length >= maxFiles}
          data-testid={testid || "file-upload-btn"}
          className="inline-flex items-center gap-2 px-4 py-2 rounded-lg border-2 border-dashed border-[#E5E7EB] hover:border-[#0B3676] text-sm font-semibold text-[#08285A] transition-colors disabled:opacity-50"
        >
          {uploading ? <Loader2 size={16} className="animate-spin" /> : <Upload size={16} />}
          {uploading ? "Yükleniyor..." : label}
        </button>
        <span className="text-[11px] text-[#6B7280]">
          {value.length}/{maxFiles} • Max {maxSizeMB}MB
        </span>
      </div>

      {value.length > 0 && (
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3">
          {value.map((f, i) => (
            <div key={f.id || i}
                 data-testid={`uploaded-file-${i}`}
                 className="relative azd-card overflow-hidden group">
              {isImage(f) ? (
                <img src={urlFor(f)} alt={f.original_filename}
                     className="w-full h-28 object-cover" />
              ) : (
                <div className="w-full h-28 flex items-center justify-center bg-[#F6F8FB]">
                  <ImageIcon size={32} className="text-[#6B7280]" />
                </div>
              )}
              <div className="px-2 py-1.5 text-[10px] text-[#6B7280] truncate border-t border-[#E5E7EB]">
                {f.original_filename}
              </div>
              <button
                type="button"
                onClick={() => remove(i)}
                data-testid={`remove-file-${i}`}
                className="absolute top-1 right-1 p-1 rounded-full bg-[#DC2626] text-white opacity-0 group-hover:opacity-100 transition-opacity"
              >
                <X size={12} />
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
